import React, { useState, useRef, useEffect } from 'react';
import { User, ChevronDown, LayoutDashboard, UserCircle, Package, LogOut, LogIn, UserPlus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from '../../../../../context/AuthContext';
import { env } from '../../../../../config/env';

const PublicProfileMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);

  const getProfilePhotoUrl = (path: string | null | undefined): string | undefined => {
    if (!path) return undefined;
    if (path.startsWith("http")) return path;
    return `${env.IMAGE_BASE_URL}${path.replace(/^\//, "")}`;
  };


  // Fermer le menu si clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate("/auth/login");
  };

  // Liens pour utilisateur connecté
  const userItems = [
    { icon: <LayoutDashboard size={16} />, label: "Tableau de bord", path: "/user/dashboard/home" },
    { icon: <UserCircle size={16} />, label: "Mon profil", path: "/user/dashboard/profile" },
    { icon: <Package size={16} />, label: "Mes commandes", path: "/user/dashboard/orders" },
  ];

  // Liens pour utilisateur non connecté
  const guestItems = [
    { icon: <LogIn size={16} />, label: "Se connecter", path: "/auth/login" },
    { icon: <UserPlus size={16} />, label: "Créer un compte", path: "/auth/register" },
  ];

  const items = user ? userItems : guestItems;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white/30 text-white hover:bg-white/50 font-semibold p-2.5 rounded-full shadow-md hover:scale-105 transition-transform duration-200 flex items-center gap-2"
        aria-label="Menu utilisateur"
      >
        <User size={20} />
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl overflow-hidden z-50"
          >
            {user && (
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {user.first_name} {user.last_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            )}

            <div className="py-1">
              {items.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  {item.icon}
                  {item.label}
                </Link>
              ))}
              {user && (
                <>
                  <hr className="my-1 border-gray-200" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={16} />
                    Déconnexion
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PublicProfileMenu;